import { Section, VStack } from 'tio-ui/components/layout';
import PageHeader from 'tio-ui/components/page-header';
import PageControlBar from 'tio-ui/components/page-control-bar';
import SearchInput from 'tio-ui/components/search-input';
import Select from 'tio-ui/components/select';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import { findRecord } from '@warp-drive/utilities/json-api';
import { trackedFunction } from 'reactiveweb/function';
import PslfAdminCompanyFormsTable from 'tio-employee/components/pslf/admin/company-forms-table';
import type { Store as StoreService } from '@warp-drive/core';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type { IntlService } from 'ember-intl';
import type { PslfForm } from 'tio-data/data/resources/search.ext';
import type { SearchModelWith } from 'tio-data/data/resources/search.ext';
import type AdminPslfFormsController from 'tio-employee/controllers/authenticated/admin/pslf/forms';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import { PSLF_ADMIN_STATUS_FILTERS } from 'tio-employee/utils/pslf/admin-status-filters';

export interface AdminPslfFormsRouteSignature {
  Args: {
    controller: AdminPslfFormsController;
    model: SearchModelWith<PslfForm>;
  };
}

export default class AdminPslfFormsRoute extends Component<AdminPslfFormsRouteSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  formTypes = ['currentEmployer', 'priorEmployer'];

  statusFilterItems = PSLF_ADMIN_STATUS_FILTERS.map((status) => {
    return {
      value: this.intl.t(status.labelKey),
      key: status.key,
    };
  });

  get approverGroupIds() {
    return this.sessionContext.currentEmployee?.approverPslfGroupIds || [];
  }

  get groupFilterItems() {
    const groups = this.isGroupApprover
      ? this.groups.filter((group) => group.id && this.approverGroupIds.includes(group.id))
      : this.groups;

    return groups.map((group) => {
      return {
        value: group.employerLegalName,
        key: group.id,
      };
    });
  }

  formTypeFilterItems = this.formTypes.map((type) => {
    const formType = type === 'currentEmployer' ? 'current_employer' : 'prior_employer';
    return {
      value: this.intl.t(`pslf.forms.${formType}`),
      key: type,
    };
  });

  get company() {
    return this.sessionContext.currentCompany;
  }

  // The session boot does not include the company's PSLF groups.
  companyGroups = trackedFunction(this, async () => {
    const id = this.company?.id;
    if (!id) return [];
    const { content } = (await this.store.request(
      findRecord<CompanyModel>('company', id, { include: 'pslf-groups' })
    )) as { content: { data: CompanyModel } };
    return content.data.pslfGroups;
  });

  get groups() {
    return this.companyGroups.value ?? [];
  }

  get isGroupApprover() {
    return this.sessionContext.isPslfGroupApprover;
  }

  <template>
    <VStack>
      <PageHeader data-test-page-title @title={{t "pslf.forms.index_page_title"}} @sticky={{false}}>
        <:toolbar>
          <SearchInput
            data-test-search-input
            @placeholder="Search (Name, Email)"
            @value={{@controller.search}}
            @onChange={{fn (mut @controller.search)}}
            @onSearch={{fn (mut @controller.search)}}
          />
        </:toolbar>
        <:below>
          <PageControlBar>
            <:filters>
              <Select
                data-test-group-filter
                @clearable={{true}}
                @placeholder="By group"
                @items={{this.groupFilterItems}}
                @selected={{@controller.group}}
                @onChange={{fn (mut @controller.group)}}
                class="min-w-48"
              />
              <Select
                data-test-status-filter
                @clearable={{true}}
                @placeholder="By status"
                @items={{this.statusFilterItems}}
                @selected={{@controller.status}}
                @onChange={{fn (mut @controller.status)}}
                class="min-w-48"
              />
              {{#unless this.isGroupApprover}}
                <Select
                  data-test-form-type-filter
                  @clearable={{true}}
                  @placeholder="By form type"
                  @items={{this.formTypeFilterItems}}
                  @selected={{@controller.formType}}
                  @onChange={{fn (mut @controller.formType)}}
                  class="min-w-48"
                />
              {{/unless}}
            </:filters>
          </PageControlBar>
        </:below>
      </PageHeader>
      <Section class="overflow-auto">
        <:body>
          <PslfAdminCompanyFormsTable @model={{@model}} @controller={{@controller}} />
        </:body>
      </Section>
    </VStack>
  </template>
}
